import { Profile } from "../../../../core/models/entities/Profile";
import {
  BaseLogo,
  GetOverallApprovalStatus,
  getLogoOrgId,
  ImageLogo,
  LogoEditStatus,
  LogoType,
} from "../../../../core/models/entities/BaseLogo";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import { Organization } from "../../../../core/models/entities/Organization";
import { TableData } from "../../../components/GenericTable/FunctionalList";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { getLogoColumnsConfig } from "../const/logoColumnsConfig";
import { LogoRowItem } from "../components/logo-list-item.component";
import {
  FilterRecipe,
  FilterChangeEvent,
} from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import { Role } from "../../../../core/models/entities/Role";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import { organizationFilterField } from "../../../shared/const/organizationFilterField";
import {
  Color,
  Font,
  LogoOptions,
  TransferLogoType,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { useMultiSelectStateMachine } from "../../../shared/hooks/useMultiSelectStateMachine";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useState } from "react";
import { useNavigate } from "react-router";
import EmailDialog from "../../../components/Approval/EmailDialog";
import { useApiClient } from "../../../../data/clients/ApiClient";
// Removed performCavemanReFetch import - no longer needed with optimistic updates
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faPlus,
  faShare,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { AddToCartIcon } from "../../../../resources/icons/Icons";
import { useErrorToast, withToasts } from "../../../components/Toast/Toast";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { LogoPdfGenerator } from "../../../../utils/functions/pdf-generator.service";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { Menu, MenuItem } from "@mui/material";

export function useFunctionalListLogosViewModel(
  profile: Profile,
  isPageLoading: boolean,
  lastPage: number,
  options: LogoOptions,
  query: PageQuery,
  queryChanger: QueryChanger,
  onSelectLogo: (logo: BaseLogo) => void,
  onViewRejectNote: (logo: BaseLogo) => void,
  onOpenInitApproval: (logo: BaseLogo) => void,
  openCreateLogoDialog: () => void,
  customers?: Organization[],
  vendors?: Organization[],
  logos?: BaseLogo[],
  updatingLogoId?: number,
): TableData<BaseLogo> {
  const navigate = useNavigate();
  const {
    handleGoToEditLogo,
    handleDeleteLogo,
    handlePlaceVerdict,
    handleAddToCart,
    handleAddItemsToCart,
    handleRejectAndDelete,
    isLoading: isActionRunning,
  } = useLogoActionHandler();

  const { role } = useProfileContext();
  const { hidePrices, showCustomerPrices } = usePriceVisibility();
  const { generatePublicKeys, sendMultiApprovalEmail } = useApiClient();
  const apiClient = useApiClient();

  const {
    isMultiSelect,
    toggleMultiSelect,
    selectedData,
    onRowChecked,
    onMultiSelectRow,
    resetSelection,
  } = useMultiSelectStateMachine<BaseLogo>({ onSelect: onSelectLogo });

  // Email dialog state for multi-select Send Preview
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [approvalEmail, setApprovalEmail] = useState("");

  // Loading state for PDF download
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Loading state for sending logos via email
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  function handleShareClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleShareClose() {
    setAnchorEl(null);
  }

  // Validation function to check if user can perform bulk actions with current selection
  function validateSelectionCount(
    selectedCount: number,
    actionName: string,
  ): boolean {
    if (role !== Role.Admin && selectedCount > 10) {
      useErrorToast(
        `You can only ${actionName} up to 10 items at once. Please select fewer items.`,
      );
      return false;
    }
    return true;
  }

  function addToCart() {
    handleAddItemsToCart(Object.values<BaseLogo>(selectedData), 10);
  }

  function sendPreview() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (!validateSelectionCount(selectedCount, "send") || isSendingEmail) {
      return;
    }
    setEmailDialogOpen(true);
  }

  async function handleDownloadPdf() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (
      !validateSelectionCount(selectedCount, "download") ||
      isDownloadingPdf
    ) {
      return;
    }

    setIsDownloadingPdf(true);
    const selectedLogos = Object.values<BaseLogo>(selectedData);
    const vendorLogoUrl = profile.whiteLabelConfigurations?.logoUrl;

    // Prepare logo IDs with organization IDs for high-res fetching
    const logoIds = selectedLogos.map((logo) => ({
      logoId: logo.id,
      orgId: getLogoOrgId(logo),
    }));

    async function transaction() {
      return await LogoPdfGenerator.generateMultipleLogosWithHighResPdf(
        logoIds,
        apiClient,
        vendorLogoUrl,
      );
    }

    try {
      await withToasts(
        transaction,
        "Generating logos PDF",
        "Logos PDF downloaded successfully",
        "Failed to generate logos PDF",
      );
      resetSelection();
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to generate logos PDF:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  async function handleSendPreviewWithEmail() {
    const selectedLogos = Object.values<BaseLogo>(selectedData);

    setIsSendingEmail(true);

    async function transaction() {
      // Generate public keys for each selected logo using API
      const response = await generatePublicKeys(
        selectedLogos.map((logo) => logo.id),
      );
      const keysParam = response.publicKeys.join(",");
      const url = `${window.location.origin}/external-approval/logos?keys=${keysParam}`;

      // Send email with preview URL
      await sendMultiApprovalEmail(response.publicKeys, "logo", approvalEmail);

      return response;
    }

    try {
      await withToasts(
        transaction,
        "Sending email with logos",
        "Emails sent successfully",
        "Failed to send email with logos",
      );
      setEmailDialogOpen(false);
      resetSelection();
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to send preview email:", error);
    } finally {
      setIsSendingEmail(false);
    }
  }

  function placeVerdict(logo: BaseLogo, verdict: Verdict) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handlePlaceVerdict(logo, verdict);
  }

  const center = undefined; // Center prop is now empty as Create button moved to right

  const selectedCount = Object.values(selectedData)?.length || 0;
  const right = (
    <div className="flex space-x-2">
      <PrimaryButton
        key={"design-create"}
        width={"md"}
        className={"px-4"}
        onClick={openCreateLogoDialog}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span className={"flex-1"}>Create new Logo</span>
      </PrimaryButton>
      {role !== Role.Admin && (
        <PrimaryButton
          width={"md"}
          className={"px-4"}
          onClick={addToCart}
          disabled={selectedCount === 0}
        >
          <AddToCartIcon />
          <span className={"flex-1"}>Add to Order ({selectedCount})</span>
        </PrimaryButton>
      )}
      <SecondaryButton
        width={
          isDownloadingPdf || isSendingEmail
            ? "sm"
            : selectedCount > 0
              ? "xs"
              : "icon"
        }
        className={"px-4"}
        onClick={handleShareClick}
        disabled={selectedCount === 0 || isDownloadingPdf || isSendingEmail}
      >
        <FontAwesomeIcon icon={faShare} />
        <span className={"flex-1"}>
          {isDownloadingPdf
            ? "Generating..."
            : isSendingEmail
              ? "Sending..."
              : selectedCount > 0
                ? `(${selectedCount})`
                : ""}
        </span>
      </SecondaryButton>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
          <FontAwesomeIcon icon={faDownload} className="mr-2 w-4" />
          Download PDF
        </MenuItem>
        <MenuItem onClick={sendPreview} disabled={isSendingEmail}>
          <FontAwesomeIcon icon={faEnvelope} className="mr-2 w-4" />
          Send via Email
        </MenuItem>
      </Menu>
    </div>
  );

  const filters = buildLogoFilterRecipes(
    profile,
    options.transferMethods,
    vendors,
    customers,
  );

  // Now using optimistic updates in LogoRepository - no need for local deleted tracking
  const handleDeleteAndHide = async (logo: BaseLogo) => {
    await handleDeleteLogo(logo, { silent: true });
  };

  function isLogoDisabled(logo: BaseLogo): boolean {
    if (logo.logoType === LogoType.ImageLogo) {
      const imageLogo = logo as ImageLogo;
      return imageLogo.status === LogoEditStatus.Draft;
    }
    return false;
  }

  function handleViewDesigns(logo: BaseLogo) {
    navigate(
      `/design?pageSize=25&pageIndex=1&filterBy=designlogos.logoid&filterOn=${logo.id}`,
    );
  }

  const handleFilterChange = (event: FilterChangeEvent) => {
    if (profile?.userOrganizationInformations?.role === Role.Vendor) {
      const showOnlyOwned = event.activeFilters.find(
        (f) => f.key === "showOnlyOwned",
      );
      const vendorOrgId =
        profile.userOrganizationInformations.organizationId.toString();

      let newFilters = [...event.activeFilters];

      if (showOnlyOwned && showOnlyOwned.value === true) {
        newFilters = newFilters.filter(
          (f) =>
            f.key !== "showOnlyOwned" &&
            f.key !== organizationFilterField &&
            f.key !== "mainOrganizationId",
        );
        newFilters.push({
          key: "mainOrganizationId",
          value: vendorOrgId,
        });
        queryChanger.handleFilterChange({ activeFilters: newFilters });
        return;
      } else {
        newFilters = newFilters.filter((f) => f.key !== "showOnlyOwned");
        newFilters = newFilters.filter(
          (f) => !(f.key === "mainOrganizationId" && f.value === vendorOrgId),
        );
        queryChanger.handleFilterChange({ activeFilters: newFilters });
        return;
      }
    }
    queryChanger.handleFilterChange(event);
  };

  return {
    tableTitle: "Logo",
    columns: getLogoColumnsConfig(
      hidePrices,
      showCustomerPrices,
      role === Role.Admin,
      role === Role.Admin,
    ),
    onRowClick: onMultiSelectRow,
    data: logos,
    getKey: getKey,
    getRowColor: logoRowBgColor,
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    query: query,
    renderRow: (columns, data) => (
      <LogoRowItem
        columns={columns}
        logo={data}
        onPlaceVerdict={placeVerdict}
        onAddToCart={handleAddToCart}
        onEditLogo={handleGoToEditLogo}
        onSeeRejectNote={onViewRejectNote}
        onDeleteLogo={handleDeleteAndHide}
        onRejectAndDelete={handleRejectAndDelete}
        onInitiateApproval={onOpenInitApproval}
        onViewDesigns={handleViewDesigns}
        updatingLogoId={updatingLogoId}
      />
    ),
    onPageSizeChange: queryChanger.handlePageSizeChange,
    onNewSort: queryChanger.handleSortByField,
    onNewPage: queryChanger.handleNewPage,
    onFilterChange: handleFilterChange,
    isLoading: isActionRunning || isPageLoading,
    filters: filters,
    lastPage: lastPage,
    center: center,
    right: right,
    multiSelect: true,
    selectedData: selectedData,
    onRowSelected: onRowChecked,
    isRowDisabled: isLogoDisabled,
    showVendorFilter: true, // Enable vendor filter for logos
    customers: customers, // Pass customers for the customer search filter
    storageKey: "logo-list-filters",
    component: (
      <EmailDialog
        open={emailDialogOpen}
        setOpen={setEmailDialogOpen}
        email={approvalEmail}
        onEmailChange={setApprovalEmail}
        onSubmit={handleSendPreviewWithEmail}
      />
    ),
  };
}

const getKey = (logo: BaseLogo) => logo.id;

function buildLogoFilterRecipes(
  profile: Profile,
  transforMethods: TransferMethod[],
  vendor?: Organization[],
  customers?: Organization[],
) {
  const filters: FilterRecipe<any>[] = [];
  const { options: logoOptions } = useLogoContext();

  if (profile.userOrganizationInformations.role >= Role.Vendor && customers) {
    filters.push({
      options: customers,
      key: organizationFilterField,
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: "Customer",
    } as FilterRecipe<Organization>);
  }

  filters.push({
    options: [
      LogoState.Approved,
      LogoState.Rejected,
      LogoState.Pending,
      LogoState.Draft,
    ],
    key: "stateFilter",
    getOptionValue: (org) => org as string,
    getOptionDisplay: (org) => org as string,
    defaultTitle: "Status",
  } as FilterRecipe<LogoState>);

  filters.push({
    options: logoOptions.transferLogoTypes,
    key: "typeFilter",
    getOptionValue: (o) => o.typeKey,
    getOptionDisplay: (o) => o.nickname,
    defaultTitle: "Type",
  } as FilterRecipe<TransferLogoType>);

  filters.push({
    options: transforMethods,
    key: "transferMethod.key",
    getOptionValue: (org) => org.key,
    getOptionDisplay: (org) => org.displayName,
    defaultTitle: "Method",
  } as FilterRecipe<TransferMethod>);

  // Logo Classification Filter - hardcoded definitions
  const logoClassifications = [
    { value: 3, name: "Logo" },
    { value: 2, name: "Name" },
    { value: 6, name: "Numberset" },
    { value: 4, name: "Club Logo" },
    { value: 11, name: "Chain Logo" },
  ];

  filters.push({
    options: logoClassifications,
    key: "classification",
    getOptionValue: (o) => o.value.toString(),
    getOptionDisplay: (o) => o.name,
    defaultTitle: "Logo Type",
  } as FilterRecipe<{ value: number; name: string }>);

  return filters;
}

export function buildAdditionalLogoFilterRecipes(
  sizes: number[],
  colors: Color[],
  fonts: Font[],
) {
  const filters: FilterRecipe<any>[] = [];

  filters.push({
    options: sizes,
    key: "HeightMm",
    getOptionValue: (o) => o,
    getOptionDisplay: (o) => o.toString(),
    defaultTitle: "Size",
  } as FilterRecipe<number>);

  filters.push({
    options: colors,
    key: "Color.Id",
    getOptionValue: (o) => o.id,
    getOptionDisplay: (o) => o.nickname || o.name,
    defaultTitle: "Color",
  } as FilterRecipe<Color>);

  filters.push({
    options: fonts,
    key: "Font",
    getOptionValue: (o) => o.key,
    getOptionDisplay: (o) => o.name,
    defaultTitle: "Font",
  } as FilterRecipe<Font>);

  return filters;
}

function logoRowBgColor(logo: BaseLogo) {
  if (logo.logoType !== LogoType.ImageLogo) {
    return "bg-row-standard";
  }

  const imageLogo = logo as ImageLogo;

  if (imageLogo.status === LogoEditStatus.Draft) {
    return "bg-row-draft";
  }

  if (imageLogo.status === LogoEditStatus.Done) {
    return "bg-row-standard";
  }

  switch (GetOverallApprovalStatus(imageLogo.approval!)) {
    case ApprovalStatus.Rejected:
      return "bg-row-warn";
    case ApprovalStatus.Approved:
      return "bg-row-standard";
    case ApprovalStatus.Pending:
      return "bg-row-pending";
  }
}

export function getLogoState(logo: BaseLogo): LogoState {
  const imageLogo = logo as ImageLogo;

  if (imageLogo.status === LogoEditStatus.Draft) {
    return LogoState.Draft;
  }

  if (imageLogo.status === LogoEditStatus.Done) {
    return LogoState.Approved;
  }

  if (imageLogo.status === LogoEditStatus.Rejected) {
    return LogoState.Rejected;
  }

  if (imageLogo.approval === undefined) {
    return LogoState.Undefined;
  }

  switch (GetOverallApprovalStatus(imageLogo.approval!)) {
    case ApprovalStatus.Rejected:
      return LogoState.Rejected;
    case ApprovalStatus.Approved:
      return LogoState.Approved;
    case ApprovalStatus.Pending:
      return LogoState.Pending;
  }
}

export enum LogoState {
  Draft = "Draft",
  Committed = "Committed",
  Rejected = "Rejected",
  Pending = "Pending",
  Approved = "Approved",
  Undefined = "Undefined",
}
